import React from 'react';
import { Box, Typography, Skeleton, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { commandDashboard } from '../commandDashboardTokens';
import type { AttentionItem } from '../types';

interface AttentionListProps {
  items: AttentionItem[];
  totalCount: number;
  loading?: boolean;
}

export const AttentionList: React.FC<AttentionListProps> = ({ items, totalCount, loading }) => {
  const navigate = useNavigate();
  const isEmpty = !loading && totalCount === 0;

  return (
    <Box
      sx={{
        bgcolor: commandDashboard.color.innerCard,
        border: `1px solid ${commandDashboard.color.innerCardBorder}`,
        borderRadius: `${commandDashboard.radius.innerCard}px`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', p: '14px 16px', borderBottom: `1px solid ${commandDashboard.color.dividerStrong}` }}>
        <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>Needs attention</Typography>
        <Box
          component="span"
          sx={{
            px: '8px',
            py: '1px',
            borderRadius: `${commandDashboard.radius.countPill}px`,
            fontSize: '11.5px',
            fontWeight: 700,
            bgcolor: isEmpty ? commandDashboard.color.successTint : commandDashboard.color.dangerTint,
            color: isEmpty ? commandDashboard.color.successText : commandDashboard.color.dangerText,
          }}
        >
          {totalCount}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: '14px 16px' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={54} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : isEmpty ? (
        <Box sx={{ p: '20px 16px', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: commandDashboard.color.inkSubtle }}>Nothing needs attention right now.</Typography>
        </Box>
      ) : (
        items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              p: '12px 16px',
              borderBottom: `1px solid ${commandDashboard.color.dividerLight}`,
              bgcolor: item.severity === 'blocking' ? commandDashboard.color.dangerRow : '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  mt: '5px',
                  flex: '0 0 auto',
                  bgcolor: item.severity === 'blocking' ? commandDashboard.color.dotRed : commandDashboard.color.dotAmber,
                }}
              />
              <Typography sx={{ fontSize: '13.5px', fontWeight: 600, lineHeight: 1.35 }}>{item.title}</Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: commandDashboard.color.inkSubtle, pl: '15px', lineHeight: 1.4 }}>
              {item.meta}
            </Typography>
            <MuiLink
              component="button"
              onClick={() => navigate(item.href)}
              underline="hover"
              sx={{
                fontSize: '12.5px',
                fontWeight: 600,
                color: commandDashboard.color.accent,
                pl: '15px',
                textAlign: 'left',
                transition: 'color 120ms ease',
                '&:hover': { color: commandDashboard.color.accentHover },
              }}
            >
              {item.action}
            </MuiLink>
          </Box>
        ))
      )}
    </Box>
  );
};
